import React, { ReactNode, Suspense, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useSystem } from '@contexts/SystemContext'
import { useNotification } from '@contexts/NotificationContext'
import { useTimer } from '@contexts/TimerContext'
import NotificationDropdown from '@components/Notifications/NotificationDropdown'
import TimerHeaderWidget from '@components/Timer/TimerHeaderWidget'
import { timeUtils } from '@services/timerService'
import { getDepartureInfo } from '@utils/departureStatus'
import {
  Clock,
  FolderOpen,
  FileText,
  BarChart3,
  Settings,
  Users,
  Bell,
  LogOut,
  CheckSquare,
  ShieldCheck,
  Menu,
  Map,
  DollarSign,
  Gift,
  TrendingUp,
  FileSignature,
  Target,
  Wallet,
  Droplet,
  Award,
  Building2,
  Megaphone,
  BookOpen,
  CalendarClock,
  Receipt
} from 'lucide-react'

interface LayoutProps {
  children: ReactNode
}

type ViewMode = 'work' | 'admin'

interface NavItem {
  path: string
  label: string
  icon: any
  roles?: ('ADMIN' | 'MANAGER')[]
  isAdminReview?: boolean // 標記為管理員審核功能
  mode?: ViewMode | 'both' // 屬於哪個模式（不填視為 both）
}

interface NavGroup {
  title: string
  items: NavItem[]
}

const VIEW_MODE_STORAGE_KEY = 'anytime_view_mode'

// 解析頭貼來源（同 AvatarUpload 邏輯）
const resolveAvatarSrc = (avatar?: string | null): string | null => {
  if (!avatar) return null
  if (avatar.startsWith('data:') || avatar.startsWith('http://') || avatar.startsWith('https://')) {
    return avatar
  }
  return avatar.startsWith('/') ? avatar : `/${avatar}`
}

// Header 頭貼徽章（avatar 圖片或姓名首字 fallback）
const HeaderAvatar: React.FC<{ avatar?: string | null; name?: string | null; size?: number }> = ({ avatar, name, size = 28 }) => {
  const src = resolveAvatarSrc(avatar)
  const [errored, setErrored] = useState(false)
  const initials = (name || '用')
    .trim()
    .split(/\s+/)
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name || '用戶'}
        onError={() => setErrored(true)}
        className="rounded-full object-cover ring-2 ring-white shadow-sm"
        style={{ width: size, height: size }}
      />
    )
  }

  return (
    <div
      className="rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white font-semibold flex items-center justify-center ring-2 ring-white shadow-sm"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials}
    </div>
  )
}

// 今日總計統計元件
const TodayStatsWidget: React.FC = () => {
  const { totalTodayDuration } = useTimer()
  
  return (
    <div className="time-entry-stats-badge">
      <div className="flex items-center gap-2.5 whitespace-nowrap">
        <div className="time-entry-stats-icon flex-shrink-0">
          <TrendingUp className="w-3.5 h-3.5" />
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-secondary-400 font-medium">
            今日總計
          </span>
          <span className="text-sm font-bold text-secondary-800 font-mono leading-tight">
            {timeUtils.formatDurationHuman(totalTodayDuration)}
          </span>
        </div>
      </div>
    </div>
  )
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const { user, logout } = useAuth()
  const { systemSettings } = useSystem()
  const { unreadCount: notificationCount } = useNotification()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false)
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(VIEW_MODE_STORAGE_KEY) : null
    return saved === 'admin' ? 'admin' : 'work'
  })

  const isAdmin = user?.role === 'ADMIN'

  const setViewMode = (mode: ViewMode, persist = true) => {
    setViewModeState(mode)
    if (persist) {
      try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode) } catch {}
    }
  }

  // 獲取軟體名稱，如果還沒載入則使用預設值
  const softwareName = systemSettings?.softwareName || 'AnyTime'

  // 導航分組配置
  const navigationGroups: NavGroup[] = [
    {
      title: '時間管理',
      items: [
        { path: '/timer', label: '計時器', icon: Clock, mode: 'work' },
        { path: '/timesheets', label: '工時申報', icon: FileText, mode: 'work' },
        { path: '/office', label: '虛擬辦公室', icon: Building2, mode: 'work' }
      ]
    },
    {
      title: '審批',
      items: [
        {
          path: '/approvals/timesheets',
          label: '工時審批',
          icon: CheckSquare,
          roles: ['ADMIN', 'MANAGER'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/benefit-approvals',
          label: '福利審批',
          icon: Gift,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/expense-approvals',
          label: '費用審核',
          icon: Receipt,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/approvals/salaries',
          label: '月申報審批',
          icon: DollarSign,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/documents/signatures',
          label: '文件審批',
          icon: CheckSquare,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/employer-cost',
          label: '人事成本',
          icon: ShieldCheck,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        }
      ]
    },
    {
      title: '後台管理',
      items: [
        {
          path: '/admin/goals',
          label: '目標設定',
          icon: Target,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/bonus-pools',
          label: '獎金池管理',
          icon: Wallet,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/allowances',
          label: '固定加給',
          icon: CalendarClock,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/performance',
          label: '事蹟管理',
          icon: Award,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/announcements',
          label: '公告管理',
          icon: Megaphone,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/wiki',
          label: '知識庫管理',
          icon: BookOpen,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/level-maps',
          label: '夥伴地圖管理',
          icon: Map,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        }
      ]
    },
    {
      title: '發款管理',
      items: [
        { path: '/salaries', label: '發款紀錄', icon: DollarSign, mode: 'work' },
        { path: '/benefits', label: '福利中心', icon: Gift, mode: 'work' },
        { path: '/expenses', label: '費用申報', icon: Receipt, mode: 'work' },
        { path: '/bonus-pool', label: '我的獎金湖', icon: Droplet, mode: 'work' }
      ]
    },
    {
      title: '人事預算',
      items: [
        {
          path: '/projects',
          label: '專案管理',
          icon: FolderOpen,
          roles: ['ADMIN', 'MANAGER'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/users',
          label: '用戶管理',
          icon: Users,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        },
        {
          path: '/admin/documents/templates',
          label: '文件管理',
          icon: FileSignature,
          roles: ['ADMIN'],
          isAdminReview: true,
          mode: 'admin'
        }
      ]
    },
    {
      title: '升等與事蹟',
      items: [
        { path: '/partner-map', label: '夥伴升等', icon: Map, mode: 'work' },
        { path: '/my-goals', label: '目標達成', icon: Target, mode: 'work' },
        { path: '/performance', label: '事蹟卡片', icon: TrendingUp, mode: 'work' }
      ]
    },
    {
      title: '文件與紀錄',
      items: [
        { path: '/wiki', label: '知識庫', icon: BookOpen, mode: 'both' },
        { path: '/announcements', label: '公司公告', icon: Megaphone, mode: 'work' },
        { path: '/documents', label: '我的文件', icon: FileText, mode: 'work' },
        { path: '/reports', label: '報表分析', icon: BarChart3, mode: 'both' }
      ]
    },
  ]

  // 權限檢查函數
  const hasPermission = (item: NavItem): boolean => {
    if (!item.roles) return true
    return item.roles.includes(user?.role as any)
  }

  // 模式過濾：管理員依當前模式顯示對應項目；其他角色不受模式影響
  const matchesMode = (item: NavItem): boolean => {
    if (!isAdmin) return true
    if (!item.mode || item.mode === 'both') return true
    return item.mode === viewMode
  }

  // 分組過濾函數（過濾掉所有項目都無權限的分組）
  const filterGroups = (groups: NavGroup[]): NavGroup[] => {
    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(item => hasPermission(item) && matchesMode(item))
      }))
      .filter(group => group.items.length > 0)
  }

  const isActivePath = (path: string) => {
    if (path === '/timer') {
      return location.pathname === '/' || location.pathname.startsWith('/timer')
    }
    return location.pathname.startsWith(path)
  }

  // 路徑變化時自動切換模式（不寫入 localStorage）
  useEffect(() => {
    if (!isAdmin) return
    const allItems = navigationGroups.flatMap(g => g.items)
    const currentItem = allItems.find(i => isActivePath(i.path))
    if (!currentItem || !currentItem.mode || currentItem.mode === 'both') return
    if (currentItem.mode !== viewMode) {
      setViewMode(currentItem.mode, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdmin])

  const handleLogout = () => {
    logout()
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const closeSidebar = () => {
    setIsSidebarOpen(false)
  }

  const handleNavClick = () => {
    // 在手機版點擊導航後關閉側邊欄
    if (window.innerWidth < 768) {
      closeSidebar()
    }
  }

  // 監聽螢幕尺寸變化
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleNotificationDropdown = () => {
    setShowNotificationDropdown(!showNotificationDropdown)
  }

  const closeNotificationDropdown = () => {
    setShowNotificationDropdown(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      {/* 手機版頂部 Header */}
      <header className="md:hidden bg-white shadow-soft border-b border-primary-200 px-4 py-4 fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleSidebar}
              className="btn-ghost p-2"
              aria-label="開啟選單"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="block">
              <h1 className="text-xl font-bold text-gradient">{softwareName}</h1>
            </Link>
            {/* Mobile Timer Widget */}
            <TimerHeaderWidget />
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button 
                className="btn-ghost relative p-2"
                onClick={toggleNotificationDropdown}
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
              {showNotificationDropdown && (
                <NotificationDropdown 
                  onClose={closeNotificationDropdown}
                />
              )}
            </div>
            <div className="relative group">
              <button className="btn-ghost flex items-center space-x-2 p-1.5">
                <HeaderAvatar avatar={user?.avatar} name={user?.name} size={28} />
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-primary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 hover:bg-primary-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>個人設定</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>登出</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 手機版背景遮罩 */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden"
          onClick={closeSidebar}
        />
      )}

      <div className="flex md:pt-0 pt-20">
        {/* 側邊欄 - 平板端摺疊為圖示模式，hover 展開 */}
        <aside className={`
          fixed md:relative left-0 bg-white shadow-soft border-r border-primary-200 z-40
          transform transition-all duration-300 ease-in-out shrink-0
          w-64 md:w-16 lg:w-64
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:min-h-screen md:top-0 md:h-auto
          top-[65px] h-[calc(100vh-65px)] overflow-y-auto
          md:hover:w-64 md:hover:shadow-lg group/sidebar
        `}>
          <div className="p-6 hidden md:block">
            <Link to="/" className="block">
              <h1 className="text-2xl font-bold text-gradient hidden lg:block group-hover/sidebar:block">{softwareName}</h1>
              <h1 className="text-xl font-bold text-gradient lg:hidden block group-hover/sidebar:hidden text-center">AT</h1>
              <p className="text-sm text-secondary-600 mt-1 hidden lg:block group-hover/sidebar:block">計時管理系統</p>
            </Link>
          </div>

          <nav className="px-4 lg:px-4 md:px-2 pb-6 pt-4 md:pt-0 group-hover/sidebar:px-4">
            {/* 管理員模式切換 - 平板摺疊時隱藏，hover 展開時顯示 */}
            {isAdmin && (
              <div className="mb-4 md:hidden lg:block group-hover/sidebar:block">
                <div className="flex bg-secondary-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('work')}
                    className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                      viewMode === 'work'
                        ? 'bg-white text-primary-600 shadow-sm font-medium'
                        : 'text-secondary-600 hover:text-secondary-800'
                    }`}
                  >
                    工作模式
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('admin')}
                    className={`flex-1 text-sm py-1.5 rounded-md transition-colors ${
                      viewMode === 'admin'
                        ? 'bg-white text-red-600 shadow-sm font-medium'
                        : 'text-secondary-600 hover:text-secondary-800'
                    }`}
                  >
                    管理模式
                  </button>
                </div>
              </div>
            )}
            {filterGroups(navigationGroups).map((group, groupIndex) => (
              <div key={group.title}>
                {/* 分組標題 - 平板摺疊時隱藏 */}
                <div className={`
                  text-xs font-semibold text-secondary-500
                  uppercase tracking-wider px-2
                  ${groupIndex > 0 ? 'mt-6' : 'mt-0'}
                  mb-2
                  md:hidden lg:block group-hover/sidebar:block
                `}>
                  {group.title}
                </div>
                {/* 平板摺疊時的分隔線 */}
                {groupIndex > 0 && (
                  <div className="hidden md:block lg:hidden group-hover/sidebar:hidden my-3 mx-2 border-t border-secondary-200" />
                )}

                {/* 導航項目 */}
                <div className="space-y-1">
                  {group.items.map(item => {
                    const Icon = item.icon
                    const isActive = isActivePath(item.path)

                    // 只有管理員才會看到審核功能的紅色邊框標示
                    const showAdminReviewStyle = item.isAdminReview && user?.role === 'ADMIN'
                    const adminReviewBorder = showAdminReviewStyle ? 'border-l-2 border-red-400' : ''

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-link ${isActive ? 'nav-link-active' : ''} ${adminReviewBorder} justify-start md:justify-center lg:justify-start group-hover/sidebar:justify-start px-4 md:px-2 lg:px-4 group-hover/sidebar:px-4`}
                        onClick={handleNavClick}
                        title={item.label}
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        <span className="inline md:hidden lg:inline group-hover/sidebar:inline whitespace-nowrap">{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Header - 只在桌面版顯示 */}
          <header className="hidden md:block bg-white shadow-soft border-b border-primary-200 px-4 lg:px-6 py-3 lg:py-4">
            <div className="flex items-center justify-between gap-2">
              {/* 左側：計時器和統計 */}
              <div className="flex items-center gap-2 lg:gap-4 min-w-0">
                {/* Timer Header Widget */}
                <TimerHeaderWidget />
                
                {/* Today Total Duration Stats Badge - 較大螢幕才顯示 */}
                <div className="hidden lg:block">
                  <TodayStatsWidget />
                </div>
              </div>
              
              {/* 右側：操作按鈕 */}
              <div className="flex items-center gap-1 lg:gap-2 xl:gap-4 flex-shrink-0">
                {/* Quick Timer Button */}
                <Link
                  to="/timer"
                  className="btn-primary whitespace-nowrap text-sm lg:text-base px-3 lg:px-4 py-2"
                >
                  <span className="hidden xl:inline">開始計時</span>
                  <span className="xl:hidden">計時</span>
                </Link>
                
                {/* View Reports Button - 較大螢幕才顯示文字 */}
                <Link
                  to="/reports"
                  className="btn-ghost whitespace-nowrap text-sm lg:text-base px-3 lg:px-4 py-2"
                >
                  <span className="hidden lg:inline">查看報表</span>
                  <BarChart3 className="w-5 h-5 lg:hidden" />
                </Link>
                
                {/* Notifications */}
                <div className="relative">
                  <button 
                    className="btn-ghost relative p-2"
                    onClick={toggleNotificationDropdown}
                  >
                    <Bell className="w-5 h-5" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {notificationCount > 99 ? '99+' : notificationCount}
                      </span>
                    )}
                  </button>
                  {showNotificationDropdown && (
                    <NotificationDropdown 
                      onClose={closeNotificationDropdown}
                    />
                  )}
                </div>
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="btn-ghost flex items-center gap-1.5 lg:gap-2 p-1.5 lg:pr-3 lg:pl-1.5">
                    <HeaderAvatar avatar={user?.avatar} name={user?.name} size={28} />
                    <span className="hidden xl:inline max-w-[100px] truncate">{user?.name || '用戶'}</span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-primary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      <Link
                        to="/settings"
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-secondary-700 hover:bg-primary-50 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>個人設定</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>登出</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* 離職狀態 Banner */}
          {(() => {
            const dep = getDepartureInfo(user?.departureDate)
            if (dep.status === 'NOTICE_PERIOD') {
              return (
                <div className="bg-orange-50 border-b border-orange-200 text-orange-800 px-4 lg:px-6 py-3 text-sm flex items-center justify-between gap-3">
                  <span>離職倒數 <strong>{dep.daysUntilDeparture}</strong> 天</span>
                </div>
              )
            }
            if (dep.status === 'READONLY') {
              return (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 lg:px-6 py-3 text-sm flex items-center justify-between gap-3">
                  <span>已離職唯讀模式 — <strong>{dep.daysUntilLock}</strong> 天後帳號鎖定</span>
                </div>
              )
            }
            return null
          })()}

          {/* Page Content — 內層 Suspense 接住 lazy 頁面載入，側欄/header 保持可見不被外層 fallback 蓋掉 */}
          <div className="px-4 py-4 md:px-6 md:py-8">
            <Suspense
              fallback={
                <div className="flex items-center justify-center min-h-[40vh]">
                  <div className="w-10 h-10 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin" />
                </div>
              }
            >
              {children}
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
